import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Star, Check, X, Plus, Loader2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { key: 'pending',  label: 'Pending',  cls: 'bg-yellow-100 text-yellow-700' },
  { key: 'approved', label: 'Approved', cls: 'bg-green-100 text-green-700' },
  { key: 'rejected', label: 'Rejected', cls: 'bg-red-100 text-red-700' },
];

export function AdminTestimonialsTab() {
  const [all, setAll] = useState([]);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/testimonials`);
      const d = await r.json();
      setAll(d.testimonials || []);
    } catch (e) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => all.filter(t => t.status === tab), [all, tab]);
  const counts = useMemo(() => ({
    pending: all.filter(t => t.status === 'pending').length,
    approved: all.filter(t => t.status === 'approved').length,
    rejected: all.filter(t => t.status === 'rejected').length,
  }), [all]);

  const updateStatus = async (id, status) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/testimonials/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error('Failed');
      toast.success(`Moved to ${status}`);
      load();
    } catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this testimonial permanently?')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/testimonials/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Failed');
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Card className="mb-8">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle>Testimonials</CardTitle>
          <Button onClick={() => setShowAdd(true)} size="sm" data-testid="add-testimonial-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add manual
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Tabs */}
        <div className="flex gap-2 mb-5 border-b border-gray-200 dark:border-gray-800">
          {STATUS_TABS.map(({ key, label }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`status-tab-${key}`}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === key
                  ? 'border-purple-600 text-purple-700 dark:text-purple-300'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
              }`}>
              {label} <span className="text-xs text-gray-400 ml-1">({counts[key]})</span>
            </button>
          ))}
        </div>

        {loading
          ? <div className="text-center text-gray-400 py-10">Loading…</div>
          : filtered.length === 0
            ? <div className="text-center text-gray-400 py-10">No {tab} testimonials.</div>
            : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((t) => (
                  <div key={t.id} className="border border-gray-200 dark:border-gray-800 rounded-xl p-4 bg-white dark:bg-gray-950"
                    data-testid={`testimonial-${t.id}`}>
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={`s-${i}`} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}
                    </div>
                    <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed mb-3">"{t.text}"</p>
                    <div className="text-xs text-gray-700 dark:text-gray-300 font-semibold">{t.name}</div>
                    {t.company && <div className="text-[11px] text-gray-500">{t.company}</div>}
                    <div className="text-[10px] text-gray-400 mt-1">
                      {t.source} · {t.consent_to_publish ? 'consent ✓' : 'no consent'} · {new Date(t.submitted_at).toLocaleDateString()}
                    </div>
                    <div className="flex gap-1.5 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                      {tab !== 'approved' && t.consent_to_publish && (
                        <Button size="sm" variant="outline" className="flex-1 text-green-700 border-green-200 hover:bg-green-50" onClick={() => updateStatus(t.id, 'approved')} data-testid={`approve-${t.id}`}>
                          <Check className="h-3 w-3 mr-1" /> Approve
                        </Button>
                      )}
                      {tab !== 'rejected' && (
                        <Button size="sm" variant="outline" className="flex-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => updateStatus(t.id, 'rejected')} data-testid={`reject-${t.id}`}>
                          <X className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-600" onClick={() => remove(t.id)} data-testid={`delete-${t.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
      </CardContent>

      <AddTestimonialDialog open={showAdd} onOpenChange={setShowAdd} onCreated={load} />
    </Card>
  );
}

function AddTestimonialDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [status, setStatus] = useState('approved');
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(''); setCompany(''); setRating(5); setText(''); setStatus('approved'); };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || text.trim().length < 8) {
      toast.error('Need a name and at least one sentence.');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/testimonials`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, company, rating, text, status }),
      });
      if (!r.ok) throw new Error('Failed');
      toast.success('Testimonial added.');
      reset();
      onOpenChange(false);
      onCreated();
    } catch { toast.error('Failed to add'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-testimonial-dialog">
        <DialogHeader><DialogTitle>Add manual testimonial</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(n => (
              <button key={n} type="button" onClick={() => setRating(n)} className="p-1">
                <Star className={`w-6 h-6 ${rating >= n ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
              </button>
            ))}
          </div>
          <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Name" data-testid="manual-name" />
          <Input value={company} onChange={(e)=>setCompany(e.target.value)} placeholder="Company (optional)" data-testid="manual-company" />
          <Textarea value={text} onChange={(e)=>setText(e.target.value)} placeholder="Their words…" rows={3} maxLength={280} data-testid="manual-text" />
          <div>
            <Label className="text-xs">Status</Label>
            <select value={status} onChange={(e)=>setStatus(e.target.value)} className="w-full mt-1 border rounded-md p-2 text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700" data-testid="manual-status">
              <option value="approved">Approved (show on site)</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <Button type="submit" disabled={saving} className="w-full" data-testid="manual-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add testimonial'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
